import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { t } from '../../../../types/translation/Translator';
import Table from '../../../components/Table';
import { AssetContext } from '../../../../context/AssetContext';
import { Asset } from '../../../../types/asset/asset';
import { AssetFilter } from '../../../../VentoryUI/components/filters/AssetFilter/AssetFilter';
import { AssetTable } from '../../Common/AssetTable';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useNavigate, useParams } from 'react-router-dom';
import CreateAssetModal from '../Modals/CreateAssetModal';
import { UserContext } from '../../../../context/UserContext';
import AssignAssetModal from '../Modals/AssignAssetModal';
import ReturnAssetModal from '../Modals/ReturnAssetModal';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import { AssetTransactionStatus } from '../../../../types/asset_transaction/assetTransaction';
import { AssetTransactionUtil } from '../../../../types/asset_transaction/assetTransaction.util';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { Routing } from '../../../../util/routes.util';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import AssetTransactionUpdateModal from '../Modals/AssetTransactionUpdateModal';
import { UnitOfMeasure } from '../../../../types/uom';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';
import { CreateButton } from '../../../components/Button';

export default function AssetDetailPane() {
  const pmdId = useParams()['id'] || '';

  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { assets, assetsLoading } = useContext(AssetContext);
  const { assetTransactions, assetTransactionsLoading } = useContext(AssetTransactionContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tagRelations } = useContext(TagRelationContext);

  const assetTableSettings = getTableSettings(EntityType.asset, currentCompany.id);

  const filter = new AssetFilter(
    productMasterData,
    companyUsers,
    assets,
    assetTransactions,
    assetTableSettings,
    customFields,
    tagRelations,
    currentCompany,
  );

  const items = useMemo(() => {
    const assetWithTransaction = new Map(
      [...assetTransactions.values()]
        .filter(transaction => transaction.status === AssetTransactionStatus.pending && transaction.assetId)
        .map(t => [t.assetId!, t.product.unitQuantity]),
    );

    const items = [
      ...[...assets.values()]
        .map(asset => {
          const updatedAsset = new Asset(asset);
          const pmd = productMasterData.get(asset.product.productMasterDataId);
          updatedAsset.product.unitQuantity = (
            BigInt(updatedAsset.product.unitQuantity) - BigInt(assetWithTransaction.get(asset.id) || 0)
          ).toString();
          updatedAsset.product.quantity = parseFloat(
            UnitOfMeasure.toQuantity(pmd?.unitOfMeasure, updatedAsset.product.unitQuantity),
          );
          return updatedAsset;
        })
        .filter(asset => asset.product.productMasterDataId === pmdId && asset.product.quantity > 0),
      ...AssetTransactionUtil.groupByParent(
        [...assetTransactions.values()].filter(
          transaction =>
            transaction.status === AssetTransactionStatus.pending && transaction.product.productMasterDataId === pmdId,
        ),
      ).map(transaction => new Asset(transaction)),
    ];

    return items;
  }, [assets, assetTransactions, productMasterData]);

  const [filteredItems, setFilteredItems] = useState<Asset[]>(items);
  const [selected, setSelected] = useState<Asset | undefined>();

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openAssignModal, setOpenAssignModal] = useState<boolean>(false);
  const [openReturnModal, setOpenReturnModal] = useState<boolean>(false);
  const [openTransactionModal, setOpenTransactionModal] = useState<boolean>(false);

  useEffect(() => {
    if (!openCreateModal && !openAssignModal && !openReturnModal && !openTransactionModal) setSelected(undefined);
  }, [openCreateModal, openAssignModal, openReturnModal, openTransactionModal]);

  const pmd = useMemo(() => {
    return productMasterData.get(pmdId);
  }, [pmdId, productMasterData]);

  const quantity = useMemo(() => {
    return UnitOfMeasure.toQuantity(
      pmd?.unitOfMeasure,
      BigInt(items.reduce((value, asset) => BigInt(value) + BigInt(asset.product.unitQuantity), BigInt(0))).toString(),
    );
  }, [items]);

  return (
    <>
      {openCreateModal && <CreateAssetModal open={openCreateModal} setOpen={setOpenCreateModal} pmdId={pmdId} />}
      {openAssignModal && <AssignAssetModal open={openAssignModal} setOpen={setOpenAssignModal} asset={selected} />}
      {openReturnModal && <ReturnAssetModal open={openReturnModal} setOpen={setOpenReturnModal} asset={selected} />}
      {openTransactionModal && (
        <AssetTransactionUpdateModal open={openTransactionModal} setOpen={setOpenTransactionModal} asset={selected} />
      )}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='grid grid-cols-3'>
          <div>
            <p className='font-semibold'>{t(TranslationKey.productName).singular.label}</p>
            <p
              className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
              onClick={() => navigate(Routing.productMasterData.update(pmdId).info())}
              style={{ lineClamp: 2 }}
            >
              {pmd?.productName}
            </p>
          </div>
          <div className='justify-self-center'>
            <p className='font-semibold'>{t(TranslationKey.productNumber).singular.label}</p>
            <p
              className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
              onClick={() => navigate(Routing.productMasterData.update(pmdId).info())}
            >
              {pmd?.productNumber}
            </p>
          </div>
          <div className='justify-self-end'>
            <p className='font-semibold'>{t(TranslationKey.quantity).singular.label}</p>
            <p className='mt-2 text-xl font-bold text-gray-400'>
              {UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, quantity)}
            </p>
          </div>
        </div>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
            items={items}
            setItems={setFilteredItems}
            placeholder={t(TranslationKey.searchForAsset).plural.label}
            filter={filter}
          />
          <div className='flex gap-1'>
            <CreateButton onClick={() => setOpenCreateModal(true)} />
          </div>
        </div>
        <Table
          loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
          items={filteredItems}
          headers={AssetTable.header(
            productMasterData,
            companyUsers,
            assetTableSettings,
            undefined,
            locale,
            currentCompany,
          )}
          onClick={item => {
            setSelected(item);
            if (!assets.has(item.id)) {
              setOpenTransactionModal(true);
              return;
            }
            if (item.location) {
              setOpenReturnModal(true);
            } else {
              setOpenAssignModal(true);
            }
          }}
          totalItemCount={items.length}
          filter={filter}
        />
      </div>
    </>
  );
  {
    /* <FlexPane
        header={
          <Grid container>
            <Grid item xs={12} className='rounded-4 border border-ventory-light-border bg-white'>
              <Grid container className='p-4'>
                <Grid item xs={4} justifyItems={'start'}>
                  <p className='font-semibold'>{t(TranslationKey.assetName).singular.label}</p>
                  <p
                    className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
                    onClick={() => navigate(Routing.productMasterData.update(pmdId).info())}
                    style={{ lineClamp: 2 }}
                  >
                    {pmd?.productName}
                  </p>
                </Grid>
                <Grid item xs={4} justifyItems={'start'}>
                  <p className='font-semibold'>{t(TranslationKey.assetNumber).singular.label}</p>
                  <p
                    className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
                    onClick={() => navigate(Routing.productMasterData.update(pmdId).info())}
                    style={{ lineClamp: 2 }}
                  >
                    {pmd?.productNumber}
                  </p>
                </Grid>
                <Grid item xs={4} className='justify-items-end'>
                  <p className='font-semibold'>{t(TranslationKey.quantity).singular.label}</p>
                  <p className='mt-2 text-xl font-bold text-gray-400'>
                    {UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, quantity)}
                  </p>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <SearchBarWithFilter
                buttons={buttons}
                loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
                items={items}
                setItems={setFilteredItems}
                placeholder={t(TranslationKey.searchForAsset).plural.label}
                filter={filter}
              />
            </Grid>
          </Grid>
        }
        content={
          <Grid container rowSpacing={1} alignContent={'start'} className='h-full'>
            <Grid item xs={12} className='h-full'>
              <Table
                loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
                items={filteredItems}
                headers={AssetTable.header(
                  productMasterData,
                  companyUsers,
                  assetTableSettings,
                  undefined,
                  locale,
                  currentCompany,
                )}
                onClick={item => {
                  setSelected(item);
                  if (!assets.has(item.id)) {
                    setOpenTransactionModal(true);
                    return;
                  }

                  if (item.location) {
                    setOpenReturnModal(true);
                  } else {
                    setOpenAssignModal(true);
                  }
                }}
                totalItemCount={items.length}
                filter={filter}
              />
            </Grid>
          </Grid>
        }
      />
    </>
  ); */
  }
}
